import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import Button from "../components/ui/Button";
import { useCart } from "../context/CartContext";
import OrderStepper from "../components/OrderStepper";

const FREE_SHIPPING_THRESHOLD = 200;
const VALID_PROMO_CODE = "FAM26";
const DISCOUNT_RATE = 0.25;

export default function Cart() {
  useEffect(() => {
    document.title = "Cart | HD Labs";
  }, []);

  const { items, removeFromCart, setQty, totals, fam26Applied, applyPromo } =
    useCart();

  // input state only, promo applied is read from context
  const [promoInput, setPromoInput] = useState("");
  const [promoError, setPromoError] = useState("");

  const promoApplied = fam26Applied;

  const discount = useMemo(() => {
    return promoApplied ? totals.subtotal * DISCOUNT_RATE : 0;
  }, [promoApplied, totals.subtotal]);

  const discountedSubtotal = totals.subtotal - discount;
  const freeShippingUnlocked = discountedSubtotal >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = freeShippingUnlocked ? 0 : totals.shipping;
  const total = discountedSubtotal + shippingCost;

  const freeShippingLeft = Math.max(
    FREE_SHIPPING_THRESHOLD - discountedSubtotal,
    0
  );

  const freeShippingProgress =
    Math.min(discountedSubtotal / FREE_SHIPPING_THRESHOLD, 1) * 100;

  const format = (num) =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
      Number(num.toFixed(2))
    );

  const handleApplyPromo = () => {
    if (!promoInput) return;

    if (promoInput.toUpperCase() === VALID_PROMO_CODE) {
      applyPromo(VALID_PROMO_CODE);
      setPromoError("");
    } else {
      setPromoError("No code found");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-14">
          <OrderStepper currentStep={0} />
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {items.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-3xl p-16 text-center shadow-sm">
                <div className="text-2xl font-semibold text-slate-900">
                  Your cart is empty
                </div>
                <Link to="/shop" className="inline-block mt-8">
                  <Button>Browse Shop</Button>
                </Link>
              </div>
            ) : (
              items.map((i) => (
                <div
                  key={`${i.id}-${i.variant?.id || "default"}`}
                  className="bg-white border border-slate-200 rounded-3xl p-8 flex items-center gap-8 shadow-sm hover:shadow-md transition-shadow"
                >
                  <img
                    src={i.image}
                    alt={i.name}
                    className="h-28 w-28 rounded-2xl object-cover border border-slate-200"
                  />

                  <div className="flex-1">
                    <div className="text-xl font-semibold text-slate-900">
                      {i.name}
                    </div>
                    <div className="text-base text-slate-500 mt-1">
                      {format(i.price)}
                    </div>

                    <div className="mt-4 flex items-center gap-4">
                      <button
                        className="h-10 w-10 flex items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-100 transition"
                        onClick={() => setQty(i.id, i.variant?.id, i.qty - 1)}
                        disabled={i.qty <= 1}
                      >
                        −
                      </button>

                      <div className="min-w-[40px] text-center font-medium text-slate-800 text-lg">
                        {i.qty}
                      </div>

                      <button
                        className="h-10 w-10 flex items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-100 transition"
                        onClick={() => setQty(i.id, i.variant?.id, i.qty + 1)}
                      >
                        +
                      </button>

                      <button
                        className="ml-4 text-sm text-red-500 hover:text-red-600 transition"
                        onClick={() => removeFromCart(i.id, i.variant?.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm text-slate-400">Line total</div>
                    <div className="text-xl font-semibold text-slate-900 mt-2">
                      {format(i.price * i.qty)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-3xl p-10 lg:sticky lg:top-24 shadow-lg space-y-8">
              {/* Free Shipping Progress */}
              <div>
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-slate-600">Free shipping progress</span>

                  {freeShippingUnlocked ? (
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <span className="text-slate-500">{format(freeShippingLeft)} away</span>
                  )}
                </div>

                <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-2.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${freeShippingProgress}%`,
                      backgroundColor: "#2563EB",
                    }}
                  />
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-4 text-lg">
                <SummaryRow label="Subtotal" value={format(totals.subtotal)} />

                {promoApplied && (
                  <SummaryRow
                    label={`Discount (${VALID_PROMO_CODE})`}
                    value={<span className="text-green-600 font-semibold">- {format(discount)}</span>}
                  />
                )}

                <SummaryRow
                  label="Shipping"
                  value={
                    shippingCost === 0 ? (
                      <span className="text-green-600 font-semibold">Free</span>
                    ) : (
                      `$${totals.shipping.toFixed(2)}`
                    )
                  }
                />

                <div className="border-t border-slate-200 pt-5 mt-5">
                  <SummaryRow
                    label={<span className="font-semibold text-slate-900 text-xl">Total</span>}
                    value={<span className="font-semibold text-slate-900 text-xl">{format(total)}</span>}
                  />
                </div>
              </div>

              {/* Promo */}
              <div className="space-y-2 w-full">
                <label className="block text-sm text-slate-700">Promo code</label>

                <div className="w-full flex items-stretch gap-2">
                  <input
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    className="flex-1 min-w-0 rounded-xl border border-slate-300 px-4 py-0 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter code"
                    disabled={promoApplied}
                  />
                  <Button
                    onClick={handleApplyPromo}
                    disabled={promoApplied}
                    className="px-5 py-3 text-sm whitespace-nowrap"
                  >
                    {promoApplied ? "Applied" : "Apply"}
                  </Button>
                </div>

                {promoError && <p className="text-red-500 text-sm">{promoError}</p>}
                {promoApplied && <p className="text-green-600 text-sm">25% discount applied</p>}
              </div>

              <Link to="/checkout">
                <Button
                  className="w-full mt-6 text-lg py-4"
                  disabled={items.length === 0}
                >
                  Checkout
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-slate-700">{label}</div>
      <div className="text-slate-900">{value}</div>
    </div>
  );
}